<script setup lang="ts">
import { ref, computed, watch } from "vue";
import Modal from "@/components/Modal.vue";
import Button from "@/components/Button.vue";
import DataTable from "@/components/DataTable.vue"; 
import services from "@/services";
import { useToaster } from "ram-components";

const props = defineProps<{
  isOpen: boolean;
  userId: string | number;
  userName: string;
  dateFrom: string;
  dateTo: string;
  type: "scanned" | "handed_over" | "closed" | null;
}>();

const emit = defineEmits(["close"]);
const toaster = useToaster();

const isLoading = ref(false);
const items = ref<any[]>([]);

const modalTitle = computed(() => {
  if (props.type === "scanned") return `Parcels Scanned by ${props.userName}`;
  if (props.type === "handed_over") return `Parcels Handed Over by ${props.userName}`;
  if (props.type === "closed") return `Consignments Closed by ${props.userName}`;
  return "Details";
});


const reportFlag = computed(() => {
  switch (props.type) {
    case "scanned": return 1;      
    case "handed_over": return 2;  
    case "closed": return 3;      
    default: return 0;
  }
});

const tableColumns = computed(() => {
  return [
    { label: "Consignment ID", key: "consignmentID", sortable: true },
  ];
});

const loadDetails = async () => {
  if (!props.userId || !props.type) return;
  
  isLoading.value = true;
  items.value = []; 

  try {
    const payload = {
      fromDate: props.dateFrom,
      toDate: props.dateTo,
      UserID: props.userId,
      ReportFlag: reportFlag.value, 
    };

    const response = await services.getUserActivityDetails(payload);
    items.value = response.data.result || [];

  } catch (error) {
    toaster.push({ type: "error", text: "Failed to load activity details" });
  } finally {
    isLoading.value = false;
  }
};

watch(() => props.isOpen, (newVal) => {
  if (newVal) {
    loadDetails();
  }
});

const handleClose = () => {
  emit("close");
};
</script>

<template>
  <Modal :is-open="isOpen" :title="modalTitle" size="md" @close="handleClose">
    
    <div 
      class="py-2 max-h-[60vh] overflow-y-auto border-b border-gray-200"
    >
      <DataTable
        :columns="tableColumns"
        :data="items"
        :loading="isLoading"
        :pagination="false" 
        :selectable="false"
        empty-message="No records found for this user activity."
      />
    </div>

    <template #footer>
      <div class="flex justify-end">
        <Button variant="ram" @click="handleClose">
          Close
        </Button>
      </div>
    </template>
  </Modal>
</template>